import {DateTime} from 'luxon';
import {LocationWithDetails, LocationsConsumer} from '@shared/services/location.service';

export type EnhancedLocationsConsumer = LocationsConsumer & {onlineAccessLabel: string};

export type EnhancedLocationWithDetails = Omit<LocationWithDetails, 'consumers'> & {
    consumers: EnhancedLocationsConsumer[];
};

export type GroupedLocation = {
    location: LocationWithDetails['location'];
    consumers: EnhancedLocationsConsumer[];
};

export type ConsumerRowConsumer = EnhancedLocationsConsumer & {isCurrent: boolean};

export type RowKind = 'current' | 'history' | 'empty';

export type OrderedConsumerRow = {
    consumer: ConsumerRowConsumer | null;
    kind: RowKind;
};

const isPastConsumer = (
    consumer: Pick<LocationsConsumer, 'occupationEndDate'>,
    today: DateTime,
): boolean => consumer.occupationEndDate != null && consumer.occupationEndDate < today;

const isFutureConsumer = (
    consumer: Pick<LocationsConsumer, 'occupationDate'>,
    today: DateTime,
): boolean => consumer.occupationDate != null && consumer.occupationDate > today;

/** Whether the location holds past or future consumers that the given toggles leave hidden. */
export function hasHiddenHistory(
    group: GroupedLocation,
    showPast: boolean,
    showFuture: boolean,
    today: DateTime,
): boolean {
    return group.consumers.some(
        consumer =>
            (!showPast && isPastConsumer(consumer, today)) ||
            (!showFuture && isFutureConsumer(consumer, today)),
    );
}

// Lives outside both components because the table renders this list and the Excel export posts
// it: one derivation of "what is on screen", so the sheet cannot drift from the table.
export function consumerRowsInDisplayOrder(
    group: GroupedLocation,
    showPast: boolean,
    showFuture: boolean,
    today: DateTime,
): OrderedConsumerRow[] {
    const matchingCurrent: ConsumerRowConsumer[] = [];
    const matchingPast: ConsumerRowConsumer[] = [];
    const matchingFuture: ConsumerRowConsumer[] = [];
    for (const consumer of group.consumers) {
        const isPast = isPastConsumer(consumer, today);
        const isFuture = isFutureConsumer(consumer, today);
        if (isPast) {
            matchingPast.push({...consumer, isCurrent: false});
        } else if (isFuture) {
            matchingFuture.push({...consumer, isCurrent: false});
        } else {
            matchingCurrent.push({...consumer, isCurrent: true});
        }
    }

    matchingPast.sort(
        (a, b) => (b.occupationEndDate?.toMillis() ?? 0) - (a.occupationEndDate?.toMillis() ?? 0),
    );
    matchingFuture.sort(
        (a, b) => (a.occupationDate?.toMillis() ?? 0) - (b.occupationDate?.toMillis() ?? 0),
    );

    const visiblePast = showPast ? matchingPast : [];
    const visibleFuture = showFuture ? matchingFuture : [];

    let mainConsumers: (ConsumerRowConsumer | null)[];
    let historyFuture: ConsumerRowConsumer[];
    let historyPast: ConsumerRowConsumer[];

    if (matchingCurrent.length > 0) {
        mainConsumers = matchingCurrent;
        historyFuture = visibleFuture;
        historyPast = visiblePast;
    } else if (visibleFuture.length > 0 || visiblePast.length > 0) {
        mainConsumers = [...visibleFuture, ...visiblePast];
        historyFuture = [];
        historyPast = [];
    } else {
        mainConsumers = [null];
        historyFuture = [];
        historyPast = [];
    }

    const orderedConsumers: OrderedConsumerRow[] = [];
    for (const futureConsumer of historyFuture) {
        orderedConsumers.push({consumer: futureConsumer, kind: 'history'});
    }
    mainConsumers.forEach((consumer, index) => {
        let kind: RowKind;
        if (consumer == null) {
            kind = 'empty';
        } else if (consumer.isCurrent) {
            kind = 'current';
        } else {
            kind = 'history';
        }
        orderedConsumers.push({consumer, kind});
        if (index === 0) {
            for (const pastConsumer of historyPast) {
                orderedConsumers.push({consumer: pastConsumer, kind: 'history'});
            }
        }
    });

    return orderedConsumers;
}

// A login account already exists, so the invite endpoint answers WS_ALREADY_ACTIVATED.
const HAS_LOGIN_ACCOUNT = ['ACTIVATED', 'AWAIT_EMAIL', 'ACTIVE'];

export const hasLoginAccount = (consumer: Pick<LocationsConsumer, 'onlineAccess'>): boolean =>
    HAS_LOGIN_ACCOUNT.includes(consumer.onlineAccess);

export function isCurrentConsumer(
    consumer: Pick<LocationsConsumer, 'occupationDate' | 'occupationEndDate'>,
    today: DateTime,
): boolean {
    const started = consumer.occupationDate == null || consumer.occupationDate <= today;
    const notEnded = consumer.occupationEndDate == null || consumer.occupationEndDate >= today;
    return started && notEnded;
}

/** Same consumer and same move-out date: all the consumption and meter cards read from it. */
export function isSameConsumerPeriod(
    previous: Pick<LocationsConsumer, 'consumerId' | 'occupationEndDate'>,
    current: Pick<LocationsConsumer, 'consumerId' | 'occupationEndDate'>,
): boolean {
    return (
        previous.consumerId === current.consumerId &&
        previous.occupationEndDate?.toMillis() === current.occupationEndDate?.toMillis()
    );
}

/** A current consumer of a real location, named, and without a login account already. */
export function canInviteConsumer(
    consumer: Pick<LocationsConsumer, 'name' | 'onlineAccess'>,
    isCurrent: boolean,
    locationIsVirtual: boolean,
): boolean {
    return (
        isCurrent &&
        !locationIsVirtual &&
        (consumer.name ?? '').trim() !== '' &&
        !hasLoginAccount(consumer)
    );
}

export const getInviteLabelKey = (
    consumer: Pick<LocationsConsumer, 'onlineAccess'> | null,
): string =>
    consumer?.onlineAccess === 'NOT_INVITED' ? 'actions_inviteResident' : 'actions_resendInvite';
